import { AxiosRequestConfig } from 'axios'
import * as qs from 'qs'
import enGB from 'date-fns/locale/en-GB'
import {
  DiversityDimension,
  DiversityDimenstionConfig,
  FsColor,
  Module,
  ModuleConfig,
  ReportsAvailableType,
} from './model'

export const defaultLocale = enGB
export const AUTO_HIDE_DURATION = 3500

const slug = process.env.REACT_APP_SLUG
const baseUrl = process.env.REACT_APP_URL

export const BASE_URL = baseUrl
export const MEDIA_ROOT = `https://fs-${slug}-static-files.s3.eu-central-1.amazonaws.com`

export const httpConfig: AxiosRequestConfig = {
  timeout: 40000,
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: (params) => qs.stringify(params, { indices: false }),
}

export const httpMediaConfig: AxiosRequestConfig = {
  timeout: 40000,
  baseURL: MEDIA_ROOT,
  headers: {
    Accept: 'application/pdf',
  },
  responseType: 'blob',
  paramsSerializer: (params) => qs.stringify(params, { indices: false }),
}

export const fsColors: Record<string, FsColor> = {
  fsPurple: {
    key: 'fsPurple',
    primary: '#7E4880',
    light: '#A377A4',
    colors: ['#7E4880', '#A377A4', '#C9A7C9', '#EFD9EF'],
  },
  fsBlue: {
    key: 'fsBlue',
    primary: '#1953AA',
    light: '#6A7EC4',
    colors: ['#1953AA', '#6A7EC4', '#A4ADDF', '#C9CBE4'],
  },
  fsMagenta: {
    key: 'fsMagenta',
    primary: '#9F3E79',
    light: '#BD729C',
    colors: ['#9F3E79', '#BD729C', '#DAA4C1', '#F3D6E5'],
  },
  fsLightGreen: {
    key: 'fsLightGreen',
    primary: '#7C993E',
    light: '#9CB16C',
    colors: ['#7C993E', '#9CB16C', '#C6D3A5', '#E1E4D7'],
  },
  fsDarkBlue: {
    key: 'fsDarkBlue',
    primary: '#5c4691',
    light: '#8D77B5',
    colors: ['#5c4691', '#8D77B5', '#B7A7D3', '#E5DBF5', '#3D2F61', '#F4EDFF'],
  },
  fsLightOrange: {
    key: 'fsLightOrange',
    primary: '#eba133',
    light: '#f0b15e',
    colors: ['#eba133', '#f0b15e', '#f5d2aa', '#f4e3d1'],
  },
  fsGreen: {
    key: 'fsGreen',
    primary: '#2f7b44',
    light: '#67A071',
    colors: ['#2f7b44', '#67A071', '#ADCEB3', '#d1ecd4'],
  },
  fsLightBlue: {
    key: 'fsLightBlue',
    primary: '#24809d',
    light: '#5899b3',
    colors: ['#24809d', '#5899b3', '#a8cfdf', '#cfeaf6'],
  },
  fsOrange: {
    key: 'fsOrange',
    primary: '#ca390b',
    light: '#dd6740',
    colors: ['#ca390b', '#dd6740', '#f6b8a3', '#fbe0d7'],
  },
  fsGray: {
    key: 'fsGray',
    primary: '#2D2D2D',
    light: '#CECECE',
    colors: ['#2D2D2D', '#9E9E9E', '#CECECE', '#EBEBEB', '#535353'],
  },
}

export const moduleConfigMap: Map<string, ModuleConfig> = new Map<string, ModuleConfig>([
  [
    Module.PAY,
    {
      module: Module.PAY,
      fsColor: fsColors.fsLightOrange,
      unit: 'unit.wage.per.hour',
      numberFractionDigit: 2,
      fakeOptions: [
        { valueToAchieve: 95, cost: 85000 },
        { valueToAchieve: 97, cost: 95000 },
        { valueToAchieve: 99, cost: 115000 },
      ],
    },
  ],
  [
    Module.CAREER,
    {
      module: Module.CAREER,
      fsColor: fsColors.fsGreen,
      unit: 'unit.level',
      numberFractionDigit: 1,
      fakeOptions: [
        { valueToAchieve: 94, cost: 83000 },
        { valueToAchieve: 95, cost: 99000 },
        { valueToAchieve: 98, cost: 125000 },
      ],
    },
  ],
  [
    Module.WORKING_TIME,
    {
      module: Module.WORKING_TIME,
      fsColor: fsColors.fsLightBlue,
      unit: 'unit.hours.per.week',
      numberFractionDigit: 1,
      fakeOptions: [
        { valueToAchieve: 99, cost: 200000 },
        { valueToAchieve: 100, cost: 250000 },
      ],
    },
  ],
  [
    Module.TRAINING,
    {
      module: Module.TRAINING,
      fsColor: fsColors.fsOrange,
      unit: 'unit.hours.per.year',
      numberFractionDigit: 1,
      fakeOptions: [
        { valueToAchieve: 96, cost: 97000 },
        { valueToAchieve: 97, cost: 110000 },
        { valueToAchieve: 98, cost: 11300 },
      ],
    },
  ],
])

export const diversityDimensionMap: Map<string, DiversityDimenstionConfig> = new Map<
  string,
  DiversityDimenstionConfig
>([
  [
    DiversityDimension.GENDER,
    {
      diversityDimension: DiversityDimension.GENDER,
      fsColor: fsColors.fsPurple,
    },
  ],
  [
    DiversityDimension.DISABILITY,
    {
      diversityDimension: DiversityDimension.DISABILITY,
      fsColor: fsColors.fsBlue,
    },
  ],
  [
    DiversityDimension.ORIGIN,
    {
      diversityDimension: DiversityDimension.ORIGIN,
      fsColor: fsColors.fsLightGreen,
    },
  ],
  [
    DiversityDimension.MIGRATION_BACKGROUND,
    {
      diversityDimension: DiversityDimension.MIGRATION_BACKGROUND,
      fsColor: fsColors.fsLightGreen,
    },
  ],
  [
    DiversityDimension.AGE,
    {
      diversityDimension: DiversityDimension.AGE,
      fsColor: fsColors.fsMagenta,
    },
  ],
])

export enum WALKTHROUGH_STATUS {
  NotStarted,
  InProgress,
  Paused,
  Skipped,
  Done,
}

export const FAQs = {
  de: {
    Daten: [
      {
        q: 'Wie werden meine Daten analysiert?',
        a: 'Um die Analyse mit FAIR_solution durchzuführen, werden HR-Daten Ihres Unternehmens ohne die Namen der Mitarbeitenden benötigt. Die Daten werden mit unserem wissenschaftlich fundierten, diskriminierungsfreien KI-Algorithmus analysiert.',
      },
    ],
    Analyse: [
      {
        q: 'Was sind Module?',
        a: 'Wir analysieren Ihre Daten auf der Basis eines Moduls, um Gaps und potenzielle strukturelle Ungleichheiten zu identifizieren. Derzeit bietet FAIR_solution vier Module an, nämlich FAIR_Pay, FAIR_Career, FAIR_Time und FAIR_Training. FAIR_solution analysiert Ihre Daten um mögliche Gaps hinsichtlich Lohn, Karriere, Arbeitszeit oder Weiterbildung aufzuzeigen.\n\nJedes Modul wird anhand von vier Diversity Dimensionen analysiert - Gender, Behinderung, Herkunft und Alter. Zum Beispiel werden männliche und weibliche Mitarbeitende hinsichtlich Fairness beim Lohn verglichen.',
      },
      {
        q: 'Warum sehe ich nicht alle vier Module?',
        a: 'Je nach Ihrer Konfiguration sehen Sie möglicherweise nur ein oder zwei der vier Module auf Ihrem Dashboard. Kontaktieren Sie uns unter support.fs@ines-analytics.com für weitere Informationen.',
      },
      {
        q: 'Was ist der Unterschied zwischen unbereinigtem und bereinigtem Gap?',
        a: 'Der unbereinigte Pay Gap ist der Unterschied im Bruttostundenlohn zwischen zwei Gruppen, ohne individuelle Unterschiede wie Bildung oder Abteilung zu berücksichtigen. Diese Berechnung berücksichtigt nicht die Einflüsse, die im Abschnitt „Ursachen der Lücke“ beschrieben sind. Demnach handelt es sich hierbei nur um einen einfachen Vergleich, ohne mögliche Gründe für den Gap zu berücksichtigen.\n\nDer bereinigte Pay Gap berücksichtigt alle Ursachen gleichzeitig. Zum Beispiel haben zwei Mitarbeitende die gleiche Ausbildung und sind in derselben Abteilung beschäftigt...etc. Sie unterscheiden sich nur in der betrachteten Diversity Dimension (Gender). Der Gap zwischen diesen beiden Personen ist der bereinigte Gap.',
      },
      {
        q: 'Was bedeutet ein negativer Gap?',
        a: 'Ein negativer Gap bedeutet, dass Ihre Vergleichsgruppe besser abschneidet als Ihre Referenzgruppe. Zum Beispiel bei dem Gender Pay Gap könnten Sie einen negativen Gap haben, wenn die Gruppe der weiblichen Mitarbeitenden mehr verdient als die der männlichen Mitarbeitenden. Eine sehr hoher negativer Gap ist ebenfalls besorgniserregend und muss angegangen werden.',
      },
    ],
    'Gap Ursachen': [
      {
        q: 'Was sind Gap Ursachen?',
        a: 'Gap Ursachen zeigen die Art und Weise auf, wie die Merkmale den unbereinigten Gap beeinflussen. Zum Beispiel könnten weibliche Mitarbeitende einen geringeren Stundenlohn als männliche Mitarbeitende haben, weil sie weniger qualifiziert, oder weil sie kürzer im Unternehmen sind. Bessere Qualifikationen und längere Betriebszugehörigkeit stehen in der Regel mit einem höheren Stundenlohn in Verbindung, auch auf kollektivvertraglicher Basis. Werden solche Faktoren, die den Gender Pay Gap erklären können gefunden, werden sie als Gap Ursachen bezeichnet. Gap Ursachen sind sehr wichtig, um potenziellen strukturellen Bias zu verstehen und an möglichen Lösungen, wie Organisationsumstrukturierung und Mitarbeitendenbindung zu arbeiten, die praktische langfristige Möglichkeiten bieten, den Gap zu beseitigen.',
      },
      {
        q: 'Was bewirken positive und negative Gap Ursachen?',
        a: 'Wenn strukturell gleich, verringern die positiven Ursachen den unbereinigten Gap zwischen den Vergleichsgruppen, während die negativen Ursachen den unbereinigten Gap erhöhen. Die Summe aller Ursachen ergibt den Unterschied zwischen unbereinigtem und bereinigtem Gap.',
      },
    ],
    'Gap Monitoring': [
      {
        q: 'Was ist der Bereich des Gap Monitoring?',
        a: 'Der Gap wird vom Datum Ihres ältesten Datenpunktes bis zum aktuellen Datum überwacht.',
      },
      {
        q: 'Wie wird das Gap Monitoring aktualisiert?',
        a: 'Der Gap wird aktualisiert, sobald neue Daten integriert und analysiert werden. Sie erhalten einen Überblick darüber, wie sich Ihre Gaps im Laufe der Zeit entwickeln.',
      },
    ],
    Handlungsoptionen: [
      {
        q: 'Wie funktionieren Handlungsoptionen?',
        a: '„Handlungsoptionen“ ist unser KI-gestütztes Tool, das Ihnen hilft maßgeschneiderte Maßnahmen gegen den Gap zu finden. Basierend auf dem aktuellen Stand Ihres Gap zeigen wir Ihnen, welche Schritte Sie unternehmen können um den Gap zu verringern und wie viel dies kosten würde.',
      },
    ],
    Berichte: [
      {
        q: 'Welche Arten von Berichten stehen zur Verfügung und wie können sie generiert werden?',
        a: 'Unsere maßgeschneiderten Berichte bieten Ihnen herunterladbare PDF-Versionen aller Ergebnisse pro Modul mit einem einzigen Klick. Wir bieten auch gesetzliche Berichte an, die von Unternehmen eingereicht werden müssen.',
      },
      {
        q: 'Sind die Berichte editierbar?',
        a: 'Berichte sind teilweise editierbar um sicherzustellen, dass die Ergebnisse nicht manipuliert werden. Bei den allgemeinen Berichten sind die Zusammenfassungsseiten editierbar, um den angezeigten Gaps weitere detaillierte Informationen hinzuzufügen. Bei gesetzlichen Berichten gemäß den Vorlagenrichtlinien erlauben wir editierbare Seiten, um mehr Informationen pro Abschnitt hinzuzufügen.',
      },
      {
        q: 'Was soll ich tun, wenn die Erstellung eines PDF-Berichts fehlschlägt?',
        a: 'Wenn die Erstellung Ihres Berichts fehlschlägt, kann es daran liegen, dass der Dienst vorübergehend (z.B. aufgrund von Wartungsarbeiten) ausgefallen ist. Keine Sorge, Ihre Daten sind sicher gespeichert. Sie können einfach auf die Schaltfläche „Bericht herunterladen“ klicken, um die PDF-Erstellung nach ein paar Minuten erneut zu starten. Bitte kontaktieren Sie uns, wenn das Problem weiterhin bestehten sollte.',
      },
    ],
    Hilfe: [
      {
        q: 'Wo finde ich Hilfe in der Software?',
        a: 'Wenn sie noch unsicher im Umgang mit der Software sind, können Sie eine Tour durch alle Funktionen im Hilfemenü starten. Sie können die Tour von jeder Ansicht aus jederzeit starten. Nutzen sie dafür gerne auch unser Videotutorial.',
      },
    ],
  },
  en: {
    Data: [
      {
        q: 'How is my data analysed?',
        a: 'To run the FAIR_solution analysis, HR data of your company without names of the employees is needed. The data will be analysed with our science-based, non-discriminatory AI algorithm.',
      },
    ],
    Analysis: [
      {
        q: 'What are modules?',
        a: 'We analyse your data based on a module to identify Gaps and potential structural biases. Currently, FAIR_solution offers 4 Modules namely FAIR_Pay, FAIR_Career, FAIR_Time and FAIR_Training. We analyse your data to find Pay Gaps, Career Gaps, Working Time Gaps or Training Gaps.\nEach module is analysed by 4 diversity dimensions - gender, disability, origin and age. For example with gender, group of male and female employees are compared for fariness by pay.',
      },
      {
        q: 'Why do I not see all 4 modules?',
        a: 'Depending on your configuration, you might see only one or two of the four modules in your dashboard. Contact us at support.fs@ines-analytics.com for more information.',
      },
      {
        q: 'What is difference between Unadjusted and Adjusted Gap?',
        a: 'The Unadjusted Pay Gap is the difference in gross hourly wage between two groups without taking into account individual differences such as education or department. This calculation does not take into account the influences described in the section "Gap Causes". Accordingly, this is only a simple comparison without taking into account possible reasons for the gap.\nThe Adjusted Pay Gap takes all causes into account at the same time. For example, two employees have the same training and are employed in the same department...etc. They only differ in the diversity dimension under consideration (Gender). The gap between these two people is the adjusted gap.',
      },
      {
        q: 'What does a negative gap mean?',
        a: 'A negative gap means that your comparison group is doing better than your reference group. For example with gender Pay Gap, you might have a negative gap when the group of female employees are earning more than male employees. Having a really high negative gap is also concerning and needs to be addressed.',
      },
    ],
    'Gap Causes': [
      {
        q: 'What are Gap Causes?',
        a: 'Gap causes show the way in which the characteristics influence the Unadjusted Gap. For example, female employees may have less hourly wage than male employees because they are less qualified or because they have been with the company for less time. Better qualifications and longer service are usually linked to more hourly wage, also on a collectively agreed basis. If such factors are found that can explain the Gender Pay Gap, they are termed as Gap Causes.\nGap Causes are really important to understand the potential structural biases and work on possible solutions like organisational restructuring and employee retention which provide practical long-term ways to eliminate the Gap.',
      },
      {
        q: 'What do positive and negative Gap causes do?',
        a: 'When structurally equal, the positive causes reduce the Unadjusted Gap between the comparison groups while the negative causes increases the Unadjusted Gap. A total of all the causes will give you the difference between Unadjusted and Adjusted Gap.',
      },
    ],
    'Gap Monitoring': [
      {
        q: 'What is the range of Gap monitoring?',
        a: 'Gap is monitored from the date of your oldest data point until the current date.',
      },
      {
        q: 'How is Gap monitoring updated?',
        a: 'Gap is updated whenever new data is integrated and analysed. It will give you an overview of how your gaps evolve over time.',
      },
    ],
    'Options for Actions': [
      {
        q: 'How do Options for Aactions work?',
        a: 'Options For Actions is our AI-powered tool that helps you to find a tailored measure to take actions against the Gap. Based on the current status of your Gap, we will show you what steps you can take to lower the Gap and how much it would cost.',
      },
    ],
    Reports: [
      {
        q: 'What types of reports are available and how to generate them?',
        a: 'Our tailor-made reports give you downloadable PDF versions of all your results per module with a single click of a button. We also provide legal reports that need to be submitted by the companies.',
      },
      {
        q: 'Are the reports editable?',
        a: 'All reports are partially editable to make sure that the results are not manipulated. With the general reports, the summary pages are editable for adding more detailed information on the Gaps. With legal reports according to the template guidelines, we allow editable pages to add more information per section.',
      },
      {
        q: 'What should I do when the creation of a report PDF fails?',
        a: " If your report generation fails, it is because the service might be temporarily down (example: for maintenance). Don't worry, your data is securely stored. You can just click on download report button to restart the PDF generation after a few minutes. Please contact us if the problem persists.",
      },
    ],
    Help: [
      {
        q: 'Where can I find help in the software?',
        a: "If you're new to the software, you can take a tour of all the features by selecting it from the Help Menu. You can always restart the tour on any page later. You can also watch the video tutorial for better understanding of the software.",
      },
    ],
  },
}

export const ReportsAvailable: ReportsAvailableType[] = [
  {
    reportId: 1,
    reportModule: Module.PAY,
    name: 'EntgTranspG',
    isAvailable: true,
    theme: {
      main: moduleConfigMap.get(Module.PAY)?.fsColor.primary,
      graph: diversityDimensionMap.get('gender')?.fsColor.primary,
    },
  },
  {
    reportId: 2,
    reportModule: Module.PAY,
    name: 'ESG (CSR)',
    isAvailable: false,
  },
  {
    reportId: 3,
    reportModule: Module.PAY,
    name: 'LkSG',
    isAvailable: false,
  },
  {
    reportId: 4,
    reportModule: Module.PAY,
    name: 'FüPoG I',
    isAvailable: false,
  },
  {
    reportId: 5,
    reportModule: Module.PAY,
    name: 'FüPoG II',
    isAvailable: false,
  },
]
